import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';

/**
 * Check device notification permission using native Capacitor APIs
 * This provides a more reliable check than OneSignal's device state alone
 */
const checkNativeNotificationPermission = async (): Promise<boolean> => {
  try {
    // Import LocalNotifications plugin for permission checking
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    
    // Check permission status using Capacitor's native API
    const permissionStatus = await LocalNotifications.checkPermissions();
    
    
    
    // Return true if permission is granted
    return permissionStatus.display === 'granted';
  } catch (error) {
    
    return false;
  }
};

/**
 * Syncs notification state for a user at app launch or login
 * 
 * This function now handles the timing issue where permission was granted before login.
 * It will:
 * 1. Check if notification_permission_asked is false - if so, request permission properly
 * 2. Check if device has permission but OneSignal state is stale - fix the sync
 * 3. Update Supabase users table with current notification state
 * 
 * @param userId - The user ID to sync notification state for
 * @returns Promise<{ success: boolean; permissionAsked?: boolean; error?: string }>
 */
export const syncNotificationState = async (userId: string): Promise<{
  success: boolean;
  permissionAsked?: boolean;
  permissionGranted?: boolean;
  oneSignalUserId?: string;
  error?: string;
}> => {
  try {
    // Only run on native platforms
    if (!Capacitor.isNativePlatform()) {
      
      return { success: false, error: 'Not on native platform' };
    }

    

    // Get current user data to check notification_permission_asked status
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('notification_permission_asked, onesignal_user_id, notification_enabled')
      .eq('id', userId)
      .single();

    if (userError) {
      
      return { success: false, error: 'Failed to fetch user data' };
    }

    

    // Import OneSignal functions dynamically to avoid circular dependencies
    const { getOneSignalAdapter, waitForOneSignalReady } = await import('@/integrations/onesignal');
    
    // Wait for OneSignal to be ready before proceeding
    
    const isReady = await waitForOneSignalReady(10000); // 10 second timeout
    
    if (!isReady) {
      
      return { success: false, error: 'OneSignal not ready - timeout' };
    }
    
    // Get OneSignal adapter
    const adapter = getOneSignalAdapter();
    if (!adapter || !adapter.isAvailable()) {
      
      return { success: false, error: 'OneSignal adapter not available' };
    }

    // First, check if we have native permission (this could be from pre-login prompt)
    const nativePermission = await checkNativeNotificationPermission();
    

    // Get current OneSignal device state
    let deviceState: any = null;
    try {
      deviceState = await adapter.getDeviceState();
      
    } catch (deviceStateError) {
      
      return { success: false, error: 'Failed to get device state' };
    }

    if (!deviceState) {
      
      return { success: false, error: 'No device state available' };
    }

    let permissionAsked = false;
    let permissionGranted = false;
    let oneSignalUserId = deviceState.userId;

    // SCENARIO 1: Permission was granted before login (native permission = true, but OneSignal state might be wrong)
    if (nativePermission && !deviceState.hasNotificationPermission) {
      
      
      // Try to re-sync OneSignal with device permission
      try {
        // Force OneSignal to re-check permission status
        await adapter.disablePush(false); // Enable push in OneSignal
        
        // Wait a bit and get updated device state
        await new Promise(resolve => setTimeout(resolve, 1000));
        const updatedDeviceState = await adapter.getDeviceState();
        
        if (updatedDeviceState?.userId) {
          oneSignalUserId = updatedDeviceState.userId;
          permissionGranted = updatedDeviceState.hasNotificationPermission || nativePermission;
          permissionAsked = true; // User must have been asked since they have native permission
          
          
        } else {
          
          permissionGranted = nativePermission;
          permissionAsked = true;
        }
      } catch (error) {
        
        // Fall back to native permission status
        permissionGranted = nativePermission;
        permissionAsked = true;
      }
    }
    // SCENARIO 2: No permission asked yet - request it properly now that user is logged in
    else if (!userData?.notification_permission_asked) {
      
      
      try {
        // Use the new post-login permission request function
        const { requestNotificationPermissionForUser } = await import('@/integrations/onesignal');
        const result = await requestNotificationPermissionForUser();
        
        permissionGranted = result.permissionGranted;
        permissionAsked = true;
        oneSignalUserId = result.oneSignalUserId || oneSignalUserId;
        
        
      } catch (promptError) {
        
        // Fall back to current state
        permissionGranted = deviceState.hasNotificationPermission || nativePermission;
      }
    }
    // SCENARIO 3: Permission already asked and everything is in sync
    else {
      
      permissionGranted = deviceState.hasNotificationPermission || nativePermission;
      permissionAsked = userData.notification_permission_asked;
    }

    

    // Update Supabase users table
    const updateData: {
      onesignal_user_id?: string;
      notification_enabled?: boolean;
      notification_permission_asked?: boolean;
    } = {
      notification_enabled: permissionGranted
    };

    // Only update onesignal_user_id if we have one
    if (oneSignalUserId) {
      updateData.onesignal_user_id = oneSignalUserId;
    }

    // Update permission_asked status
    if (permissionAsked) {
      updateData.notification_permission_asked = true;
    }

    const { error: updateError } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);

    if (updateError) {
      
      return { success: false, error: 'Failed to update user data' };
    }

    

    return {
      success: true,
      permissionAsked,
      permissionGranted,
      oneSignalUserId
    };

  } catch (error) {
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}; 